import { Box } from '@components/grid';
import { LottieAnimator } from '@components/lottie-animator';
import PropTypes from 'prop-types';
import { useState, useEffect, useRef } from 'react';
import mildAnimation from '../lottie-animator/lotties/fire-mild-animation.json';
import hotAnimation from '../lottie-animator/lotties/fire-hot.json';
import extraHotAnimation from '../lottie-animator/lotties/fire-extraHot.json';
import plainishAnimation from '../lottie-animator/lotties/fire-plainish.json';
import bbqAnimation from '../lottie-animator/lotties/fire-BBQ.json';
import lemonHerbAnimation from '../lottie-animator/lotties/fire-lemonHerb.json';

const animationMap = {
  plainish: plainishAnimation,
  lemonherb: lemonHerbAnimation,
  mild: mildAnimation,
  hot: hotAnimation,
  extrahot: extraHotAnimation,
  bbq: bbqAnimation,
};

const BastingFireAnimation = ({ selectedOptions, duration = 2000, mx, my }) => {
  const [triggerAnimation, setTriggerAnimation] = useState(false);
  const [previousSelection, setPreviousSelection] = useState(selectedOptions);
  const [animationKey, setAnimationKey] = useState(0);
  const timeoutRef = useRef(null);

  // Get the current animation type based on the last selected option
  const getAnimationType = () => {
    if (!selectedOptions?.length) return null;
    const lastSelected = selectedOptions[selectedOptions.length - 1];
    const normalizedOption = lastSelected?.toLowerCase().replace(/[^a-z]/g, '');
    return normalizedOption in animationMap ? normalizedOption : null;
  };

  const currentAnimationType = getAnimationType();

  // Trigger animation when basting selection changes and last option should trigger animation
  useEffect(() => {
    if (
      selectedOptions?.length > 0 &&
      JSON.stringify(selectedOptions) !== JSON.stringify(previousSelection) &&
      currentAnimationType
    ) {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      setTriggerAnimation(true);
      setPreviousSelection(selectedOptions);
      // Increment animation key to force restart of Lottie animation
      setAnimationKey(prev => prev + 1);
    } else if (!currentAnimationType) {
      // If current selection doesn't trigger animation, make sure it's turned off
      setTriggerAnimation(false);
      setPreviousSelection(selectedOptions);
    }
    return undefined;
  }, [selectedOptions, previousSelection, currentAnimationType]);

  // Reset animation after delay
  useEffect(() => {
    if (triggerAnimation) {
      timeoutRef.current = setTimeout(() => {
        setTriggerAnimation(false);
        timeoutRef.current = null;
      }, duration);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [triggerAnimation, duration]);

  const getAnimationFile = () => animationMap[currentAnimationType] || mildAnimation;

  if (!triggerAnimation || !currentAnimationType) {
    return null;
  }

  return (
    <Box
      position="absolute"
      height={`calc(100% + ${my * 2}px)`}
      width={`calc(100% + ${mx * 2}px)`}
      ml={`-${mx}px`}
      mt={`-${my}px`}
      zIndex="10"
      style={{ pointerEvents: 'none' }}
    >
      <LottieAnimator
        key={animationKey}
        animationDataFile={getAnimationFile()}
        autoplay
        animationDelay={0}
        isFullHeight
      />
    </Box>
  );
};

BastingFireAnimation.propTypes = {
  selectedOptions: PropTypes.arrayOf(PropTypes.string).isRequired,
  duration: PropTypes.number,
  mx: PropTypes.number,
  my: PropTypes.number,
};

BastingFireAnimation.defaultProps = {
  duration: 2000,
  mx: 32,
  my: 32,
};

export { BastingFireAnimation };
